import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  APPLICATION,
  APPLICATION_ALL,
  APPLICATION_ANALYTICS,
  APPLICATION_CATEGORY,
  APPLICATION_CATEGORY_ALL,
  APPLICATION_CATEGORY_CREATE,
  APPLICATION_CATEGORY_DELETE,
  APPLICATION_CATEGORY_LIST,
  APPLICATION_CHANGE_COURSE,
  APPLICATION_CHANGE_COURSE_ADMIN,
  APPLICATION_CREATE,
  APPLICATION_DELETE,
  APPLICATION_FIELDS,
  APPLICATION_FIELD_BATCH,
  APPLICATION_FIELD_BATCH_CREATE,
  APPLICATION_GENERATE,
  APPLICATION_LIST,
  APPLICATION_REQUEST,
  APPLICATION_REQUEST_ALL,
  APPLICATION_REQUEST_EDIT,
  APPLICATION_REQUEST_UPDATE,
  APPLICATION_RESET_PASSWORD,
  APPLICATION_STATUS,
  APPLICATION_TYPES,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const application = createAsyncThunk(
  APPLICATION,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FORM}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.application(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCreate = createAsyncThunk(
  APPLICATION_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FORM}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationDelete = createAsyncThunk(
  APPLICATION_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FORM}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationList = createAsyncThunk(
  APPLICATION_LIST,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationAnalytics = createAsyncThunk(
  APPLICATION_ANALYTICS,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_ANALYTICS}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationAnalytics(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationAll = createAsyncThunk(
  APPLICATION_ALL,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationTypes = createAsyncThunk(
  APPLICATION_TYPES,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_TYPES}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationTypes(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationChangeCourse = createAsyncThunk(
  APPLICATION_CHANGE_COURSE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CHANGE_COURSE}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationChangeCourse(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationChangeCourseAdmin = createAsyncThunk(
  APPLICATION_CHANGE_COURSE_ADMIN,
  async (
    params: PayloadCreatorParams<`${typeof API.APPLICATION_CHANGE_COURSE_ADMIN}`, 'post'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.applicationChangeCourseAdmin(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationChangeStatus = createAsyncThunk(
  APPLICATION_STATUS,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_STATUS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationStatus(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationFieldBatch = createAsyncThunk(
  APPLICATION_FIELD_BATCH,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FIELD_BATCH}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationFieldBatch(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationFieldBatchCreate = createAsyncThunk(
  APPLICATION_FIELD_BATCH_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FIELD_BATCH}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationFieldBatchCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCategory = createAsyncThunk(
  APPLICATION_CATEGORY,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CATEGORY}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCategory(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCategoryCreate = createAsyncThunk(
  APPLICATION_CATEGORY_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CATEGORY}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCategoryCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCategoryDelete = createAsyncThunk(
  APPLICATION_CATEGORY_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CATEGORY}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCategoryDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCategoryList = createAsyncThunk(
  APPLICATION_CATEGORY_LIST,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CATEGORY}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCategoryList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationCategoryAll = createAsyncThunk(
  APPLICATION_CATEGORY_ALL,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_CATEGORY_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationCategoryAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationRequest = createAsyncThunk(
  APPLICATION_REQUEST,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_REQUEST}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationRequest(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationRequestAll = createAsyncThunk(
  APPLICATION_REQUEST_ALL,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_REQUEST_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationRequestAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationRequestEdit = createAsyncThunk(
  APPLICATION_REQUEST_EDIT,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_REQUEST_EDIT}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationRequestEdit(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationRequestUpdate = createAsyncThunk(
  APPLICATION_REQUEST_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_REQUEST_UPDATE}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationRequestUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationFields = createAsyncThunk(
  APPLICATION_FIELDS,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_FIELDS}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationFields(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationGenerate = createAsyncThunk(
  APPLICATION_GENERATE,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_GENERATE}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationGenerate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationResetPassword = createAsyncThunk(
  APPLICATION_RESET_PASSWORD,
  async (params: PayloadCreatorParams<`${typeof API.APPLICATION_RESET_PASSWORD}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.applicationResetPassword(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const applicationFieldsClear = createAction(`${APPLICATION_FIELDS}/clear`)

export const applicationClear = createAction(`${APPLICATION}/clear`)

export const applicationListClear = createAction(`${APPLICATION_LIST}/clear`)
export const applicationAnalyticsClear = createAction(`${APPLICATION_ANALYTICS}/clear`)

export const applicationAllClear = createAction(`${APPLICATION_ALL}/clear`)

export const applicationTypesClear = createAction(`${APPLICATION_TYPES}/clear`)

export const applicationFieldBatchClear = createAction(`${APPLICATION_FIELD_BATCH}/clear`)

export const applicationCategoryListClear = createAction(`${APPLICATION_CATEGORY_LIST}/clear`)

export const applicationRequestEditClear = createAction(`${APPLICATION_REQUEST_EDIT}/clear`)

export const applicationRequestUpdateClear = createAction(`${APPLICATION_REQUEST_UPDATE}/clear`)
